import { useRouteContext } from "@tanstack/react-router";
import { Icon, type IconProps } from "@/components/icon";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "@/components/ui/hover-card";
import { env } from "@/env";

function CreatorTopMenuItem({
	children,
	href,
}: {
	children: React.ReactNode;
	href: string;
}) {
	return (
		<div className="flex w-full p-1 items-center gap-2 text-sm text-white justify-center h-full text-left">
			<a
				href={href}
				target="_blank"
				className="flex w-full text-sm text-white h-full pr-8 px-2 hover:bg-zinc-700 py-0.5 rounded"
			>
				{children}
			</a>
		</div>
	);
}

function CreatorTopMenuSection({
	title,
	icon,
	children,
}: {
	title: string;
	icon: IconProps["name"];
	children: React.ReactNode;
}) {
	return (
		<HoverCard openDelay={0.4} closeDelay={0.4}>
			<HoverCardTrigger
				aria-label="Menu"
				type="button"
				className="flex w-fit items-center gap-2 text-sm text-white justify-center h-full px-2 hover:bg-zinc-700"
			>
				<Icon name={icon} className="size-3" />
				{title}
			</HoverCardTrigger>

			<HoverCardContent className="bg-zinc-950 p-0 w-fit">
				{children}
			</HoverCardContent>
		</HoverCard>
	);
}

export function CreatorTopMenu() {
	const { club } = useRouteContext({ from: "__root__" });

	if (!club?.permissions.length) {
		return null;
	}

	const isFullAdmin = club?.permissions.some((p) => p.slug === "CLUB_ADMIN");

	const adminPath = (path: string) =>
		`${env.VITE_ADMIN_URL}/c/${club.slug}${path}`;

	return (
		<div className="max-lg:hidden w-full bg-zinc-950 flex flex-row h-8">
			<a
				href={`https://painel.scarf.club/c/${club.slug}`}
				className="flex items-center gap-2 text-sm text-white justify-center h-full px-2 hover:bg-zinc-800"
				target="_blank"
			>
				<Icon name="SquareArrowOutUpRight" className="size-3" />
				Painel de Administração
			</a>

			<div className="flex-1 flex flex-row ml-16 gap-2">
				{isFullAdmin && (
					<a
						href={adminPath("/analytics")}
						className="flex w-fit items-center gap-2 text-sm text-white justify-center h-full px-2 hover:bg-zinc-800"
						target="_blank"
					>
						<Icon name="SquareArrowOutUpRight" className="size-3" />
						Relatórios
					</a>
				)}

				{isFullAdmin && (
					<CreatorTopMenuSection title="Área de Membros" icon="FileText">
						<CreatorTopMenuItem href={adminPath("/courses")}>
							Cursos
						</CreatorTopMenuItem>

						<CreatorTopMenuItem href={adminPath("/categorias")}>
							Comunidade
						</CreatorTopMenuItem>

						<CreatorTopMenuItem href={adminPath("/eventos")}>
							Eventos
						</CreatorTopMenuItem>
						<CreatorTopMenuItem href={adminPath("/ranking")}>
							Ranking
						</CreatorTopMenuItem>
						<CreatorTopMenuItem href={adminPath("/config")}>
							Personalização
						</CreatorTopMenuItem>
					</CreatorTopMenuSection>
				)}

				<a
					href={adminPath("/interacoes")}
					className="flex w-fit items-center gap-2 text-sm text-white justify-center h-full px-2 hover:bg-zinc-800"
					target="_blank"
				>
					<Icon name="SquareArrowOutUpRight" className="size-3" />
					Moderação
				</a>

				{isFullAdmin && (
					<>
						<CreatorTopMenuSection title="IA" icon="Sparkles">
							<CreatorTopMenuItem href={adminPath("/ia/geral")}>
								Geral
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/ia/acessos")}>
								Acessos
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/ia/fontes-de-dados")}>
								Fontes de Dados
							</CreatorTopMenuItem>
						</CreatorTopMenuSection>

						<CreatorTopMenuSection title="Monetização" icon="DollarSign">
							<CreatorTopMenuItem href={adminPath("/ofertas")}>
								Ofertas
							</CreatorTopMenuItem>
						</CreatorTopMenuSection>

						<CreatorTopMenuSection title="Audiência" icon="UsersRound">
							<CreatorTopMenuItem href={adminPath("/membros")}>
								Membros
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/badges")}>
								Emblemas
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/tags")}>
								Tags
							</CreatorTopMenuItem>
						</CreatorTopMenuSection>

						<CreatorTopMenuSection title="Automações" icon="Zap">
							<CreatorTopMenuItem href={adminPath("/workflows")}>
								Workflows
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/templates")}>
								Templates de Email
							</CreatorTopMenuItem>
						</CreatorTopMenuSection>

						<CreatorTopMenuSection title="Configurações" icon="Settings">
							<CreatorTopMenuItem href={adminPath("/administradores")}>
								Administradores
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/integracoes")}>
								Integrações
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/domains")}>
								Domínio
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/meu-plano")}>
								Meu Plano
							</CreatorTopMenuItem>
							<CreatorTopMenuItem href={adminPath("/afiliados")}>
								Afiliados
							</CreatorTopMenuItem>
						</CreatorTopMenuSection>
					</>
				)}
			</div>
		</div>
	);
}
